import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const Ctx = createContext({ departments: [], reload: () => {} });

const FALLBACK_COLORS = {
  Production: "#228EBF",
  Milling: "#7A4DBA",
  "3DP": "#1E8E8E",
  Reception: "#C2185B",
};

export const DepartmentsProvider = ({ children }) => {
  const [departments, setDepartments] = useState([]);
  const { user } = useAuth();

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/departments");
      setDepartments(data || []);
    } catch (e) {
      console.warn("Departments load failed:", e);
    }
  }, []);

  useEffect(() => {
    // Only fetch once the user is authenticated; the API requires a token.
    if (user) reload();
  }, [user, reload]);

  const value = useMemo(
    () => ({
      departments,
      names: departments.map((d) => d.name),
      colorFor: (name) => {
        const d = departments.find((x) => x.name === name);
        return d?.color || FALLBACK_COLORS[name] || "#001740";
      },
      allowedTypesFor: (name) => {
        const d = departments.find((x) => x.name === name);
        if (!d) return null; // null = all allowed
        const ats = d.allowed_types;
        if (!Array.isArray(ats) || ats.length === 0) return null;
        return ats;
      },
      reload,
    }),
    [departments, reload]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useDepartments = () => useContext(Ctx);
